import { BadgeOutlined } from '@mui/icons-material';
import { AlertColor, Autocomplete, Box, Chip, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import Notification from 'components/Notification';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from 'utils/axios';
import { PageProps } from 'utils/constants/data';
import { privileges } from 'utils/constants/privileges';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import ListPage from '../../CommonPage';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import ViewCompanyProfile from './ViewCompanyProfile';
import { createDropdownData } from '../../../../utils/validators/HelperFunctions';

const CompanyMasterList: FC = () => {
  const theme = useTheme();

  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openPreviewDrawer, setOpenPreviewDrawer] = useState<boolean>(false);
  const [response, setResponse] = useState<any>(null);
  const [filterDropdowns, setFilterDropdowns] = useState<any>(null);
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const selectedLocationsRef = useRef<any[]>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  useEffect(() => {
    let timer: number;
    if (message.display) {
      timer = window.setTimeout(() => {
        setMessage(prev => ({ ...prev, display: false }));
      }, 2000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [message.display]);

  const handleCustomImageMethod = async (id: number) => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getCompanyProfileById(id));
      setResponse(data);
      setOpenPreviewDrawer(true);
    } catch (err: any) {
      if (err.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: err.response?.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: err.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderPrimaryManagers = (reportingTo: any) => {
    if (!reportingTo || reportingTo.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      );
    }

    const names = reportingTo.map((r: any) => r.fullName);
    const chips = names
      .slice(0, 2)
      .map((name: string, index: number) => (
        <Chip key={index} size="small" label={name} sx={{ ml: index > 0 ? 0.5 : 0, mb: 1 }} />
      ));
    const remainingNames = names.slice(2);

    if (names.length === 1) {
      return <Chip size="small" label={names[0]} />;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        {chips}
        {remainingNames.length > 0 && (
          <Tooltip
            title={
              <Typography variant="caption" style={{ whiteSpace: 'pre-line' }}>
                {remainingNames.join('\n')}
              </Typography>
            }>
            <Chip size="small" label={`+${remainingNames.length} more`} sx={{ ml: 0.5 }} />
          </Tooltip>
        )}
      </div>
    );
  };

  const renderEmails = (contacts: any) => {
    if (!contacts || contacts.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      );
    }

    const emails = contacts.map((contact: any) => contact.email).filter(Boolean);
    const names = contacts.map((contact: any) => `${contact.firstName} ${contact.lastName}`).filter(Boolean);

    if (emails.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      );
    }

    return (
      <Box sx={{ maxWidth: '100%' }}>
        {names.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '12px', sm: '13px' },
              color: '#000',
              fontWeight: 500,
              wordBreak: 'break-all',
              lineHeight: 1.3,
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              hyphens: 'auto',
            }}>
            {names.join(', ')}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '10px', sm: '11px' },
            color: '#666',
            display: 'block',
            mt: names.length > 0 ? 0.5 : 0,
            wordBreak: 'break-all',
            lineHeight: 1.2,
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            fontStyle: 'italic',
          }}>
          {emails.join(', ')}
        </Typography>
      </Box>
    );
  };

  const CATEGORY_CHIP_LABELS: Record<string, string> = {
    A: 'A - High Priority',
    B: 'B - Medium Priority',
    C: 'C - Low',
    O: 'O - Others',
  };

  const getCategoryCode = (value: string) => {
    const code = value.trim().charAt(0).toUpperCase();
    return code === 'J' ? 'O' : code;
  };

  const getChipStyle = (code: string) => {
    switch (code) {
      case 'A':
        return {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          border: '1px solid #c8e6c9',
        };
      case 'B':
        return {
          backgroundColor: '#e3f2fd',
          color: '#1565c0',
          border: '1px solid #90caf9',
        };
      case 'C':
        return {
          backgroundColor: '#fffde7',
          color: '#f9a825',
          border: '1px solid #fff59d',
        };
      case 'O':
        return {
          backgroundColor: '#eceff1',
          color: '#37474f',
          border: '1px solid #cfd8dc',
        };
      default:
        return {
          backgroundColor: '#eeeeee',
          color: '#424242',
          border: '1px solid #bdbdbd',
        };
    }
  };

  const getOptionId = (option: any) => option?.id ?? option?.value;

  const combineLocationFilter = useCallback((baseFilter: any[] = [], locations: any[] = selectedLocationsRef.current) => {
    const filtersWithoutLocation = (baseFilter || []).filter(
      item => !(typeof item === 'string' && item.startsWith('location:')),
    );
    const cleanedFilters = filtersWithoutLocation.filter((item, itemIndex, list) => {
      if (item !== 'and') return true;
      const previous = list[itemIndex - 1];
      const next = list[itemIndex + 1];
      return Boolean(previous && next && previous !== 'and' && next !== 'and');
    });
    const locationIds = locations
      .map(location => getOptionId(location))
      .filter(value => value !== undefined && value !== null && value !== '');

    if (locationIds.length === 0) return cleanedFilters;

    const locationFilter = `location:cic:${locationIds.join(',')}`;
    return cleanedFilters.length > 0 ? [...cleanedFilters, 'and', locationFilter] : [locationFilter];
  }, []);

  const getList = useCallback(
    async (filterData: PageProps, locationOverride?: any[]) => {
      const body = filterData || { page: index + 1, size: pageSize, filter: filter, sort: ['createdAt:desc'] };
      const requestBody = {
        ...body,
        filter: combineLocationFilter(body.filter, locationOverride ?? selectedLocationsRef.current),
      };
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.post(URLS.getCompaniesPage, requestBody);
        setIsLoading(false);
        const filterDataValue = data?.content?.map((item: any) => {
          return {
            ...item,
            industryTypeName: item.industryType?.name,
            companyCategory: (() => {
              if (!item.companyCategory || typeof item.companyCategory !== 'string') {
                return '—';
              }
              const code = getCategoryCode(item.companyCategory);
              const label = CATEGORY_CHIP_LABELS[code] || item.companyCategory;
              return (
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    ...getChipStyle(code),
                    height: 24,
                    fontWeight: 600,
                    fontSize: 12,
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />
              );
            })(),
            primaryManagers: renderPrimaryManagers(item.primaryManagers),
            accountType: item.accountType,
            emails: renderEmails(item.contacts),
          };
        });
        setPageResponse({ ...data, content: filterDataValue });
      } catch (error: any) {
        setIsLoading(false);
        setPageResponse(error);
      }
    },
    [index, pageSize, filter, combineLocationFilter],
  );

  const handleLocationQuickFilter = useCallback(
    (_event: any, locations: any[]) => {
      selectedLocationsRef.current = locations;
      setSelectedLocations(locations);
      getList(
        {
          page: 1,
          size: pageSize,
          filter,
          sort: ['createdAt:desc'],
        },
        locations,
      );
    },
    [filter, getList, pageSize],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const fetchFilterDropdowns = useCallback(async () => {
    try {
      const { data: locationsData } = await axiosInstance.get(URLS.organizationLocation);
      setFilterDropdowns({
        locations: createDropdownData(locationsData ?? [], ['id', 'name']),
      });
    } catch (error: any) {
    }
  }, []);

  const pageConfig = useMemo(
    () => ({
      title: strings.lead_management,
      entityLabel: strings.lead_management,
      subtitle: strings.lead_management_list_subtitle,
      searchField: 'search',
      searchPlaceholder: 'Search by company name, category, email, or phone',
      equalSearchFilterSplit: true,
      AddButtonText: strings.lead_management_button_add,
      AddButtonUrl: routes.addCompanyMasters,
      editButtonUrl: routes.editCompanyMasters,
      listApi: URLS.getCompaniesPage,
      deleteApi: URLS.getCompanyById,
      addPrivilege: privileges.add_company_master,
      editPrivilege: privileges.edit_company_master,
      deletePrivilege: privileges.delete_company_master,
      importPrivilege: privileges.add_company_master,
      importIconOnly: true,
      importApi: URLS.importCompanies,
      downloadImportTemplate: URLS.downloadCompaniesTemplate,
      customImage: {
        imageUrl: '',
        materialIcon: <BadgeOutlined style={{ color: theme.palette.primary.main, fontSize: '20px' }} />,
        handleCustomImageMethod,
        tooltip: strings.company_profile,
        privilege: privileges.view_company_master,
      },
      headerFilters: () => (
        <Autocomplete
          multiple
          size="small"
          options={filterDropdowns?.locations ?? []}
          value={selectedLocations}
          onChange={handleLocationQuickFilter}
          getOptionLabel={(option: any) => option?.label ?? option?.name ?? ''}
          isOptionEqualToValue={(option: any, value: any) => String(getOptionId(option)) === String(getOptionId(value))}
          renderInput={params => (
            <TextField {...params} label="Location" placeholder="Search by location" />
          )}
          sx={{
            width: '100%',
            minWidth: 0,
            bgcolor: '#fff',
            '& .MuiInputBase-root': { minHeight: 36, fontSize: 13 },
          }}
        />
      ),
      filterChips: selectedLocations.length
        ? [
            {
              key: 'location',
              label: `Location : ${selectedLocations
                .map((location: any) => location?.label ?? location?.name)
                .filter(Boolean)
                .join(', ')}`,
            },
          ]
        : [],
      onClearFilterChip: (key: string) => {
        if (key === 'location') handleLocationQuickFilter(null, []);
      },
      onClearAllFilterChips: () => {
        selectedLocationsRef.current = [];
        setSelectedLocations([]);
      },
      tableColumn: [
        {
          field: 'companyName',
          headerName: strings.lead_management_contact_details_company_name,
          flex: 1.4,
          sortable: true,
        },
        {
          field: 'companyCategory',
          headerName: 'Category',
          flex: 1.2,
          sortable: true,
        },
        {
          field: 'industryTypeName',
          headerName: strings.lead_management_contact_details_industry_type,
          flex: 1,
          sortable: true,
          localField: 'industryType.localizedAttributes.name',
        },
        { field: 'accountType', headerName: strings.lead_management_type_of_account, flex: 1, sortable: true },
        {
          field: 'branchCount',
          headerName: 'Branches',
          flex: 0.6,
          sortable: false,
        },
        { ...lastUpdatedTableColumn(), flex: 1.1 },
      ],
    }),
    [filterDropdowns?.locations, handleLocationQuickFilter, selectedLocations, theme.palette.primary.main],
  );

  useEffect(() => {
    fetchFilterDropdowns();
  }, [fetchFilterDropdowns]);

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage isLoading={isLoading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
      {openPreviewDrawer && (
        <ViewCompanyProfile
          data={response}
          handleClose={() => setOpenPreviewDrawer(false)}
          openPdfDrawer={openPreviewDrawer}
        />
      )}
    </>
  );
};
export default CompanyMasterList;
